import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);

    // Log error to backend
    this.logError(error, errorInfo);
  }

  private async logError(error: Error, errorInfo: ErrorInfo) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      await supabase.functions.invoke("log-error", {
        body: {
          error_type: "react",
          error_message: error.message,
          stack_trace: error.stack || errorInfo.componentStack,
          request_path: window.location.pathname,
          severity: "high"
        },
        headers: session ? {
          Authorization: `Bearer ${session.access_token}`
        } : undefined
      });
    } catch (logError) {
      console.error("Failed to log error:", logError);
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="flex justify-center">
              <AlertTriangle className="w-16 h-16 text-destructive" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-bold">Qualcosa è andato storto</h1>
              <p className="text-muted-foreground">
                Si è verificato un errore imprevisto. Il nostro team è stato notificato e risolveremo il problema al più presto.
              </p>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <div className="bg-muted p-4 rounded-lg text-left overflow-auto max-h-48">
                <p className="text-sm font-mono text-destructive">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => window.location.reload()} variant="outline">
                Ricarica la pagina
              </Button>
              <Button onClick={this.handleReset}>
                Torna alla home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
